'use client';

import type { SlotApi } from './types';

type SlotGroup = { label: string; slots: SlotApi[] };

function groupByPart(slots: SlotApi[], tz: string): SlotGroup[] {
  const groups: { morning: SlotApi[]; afternoon: SlotApi[]; evening: SlotApi[] } = {
    morning: [],
    afternoon: [],
    evening: [],
  };
  for (const s of slots) {
    const hour = Number(
      new Intl.DateTimeFormat('en-US', { timeZone: tz, hour: 'numeric', hour12: false }).format(
        new Date(s.startUtc)
      )
    );
    if (hour < 12) groups.morning.push(s);
    else if (hour < 17) groups.afternoon.push(s);
    else groups.evening.push(s);
  }
  return [
    { label: 'MORNING', slots: groups.morning },
    { label: 'AFTERNOON', slots: groups.afternoon },
    { label: 'EVENING', slots: groups.evening },
  ];
}

function formatTime(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

function formatLongDate(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).format(new Date(iso));
}

export function SlotPicker({
  slotsForDay,
  clientTimezone,
  ownerTimezone,
  selectedStartUtc,
  onSelect,
  selectedDateIso,
  loading,
}: {
  slotsForDay: SlotApi[];
  clientTimezone: string;
  ownerTimezone: string;
  selectedStartUtc: string | null;
  onSelect: (slot: SlotApi) => void;
  selectedDateIso: string | null;
  loading: boolean;
}) {
  // Branch 1: no date selected yet
  if (!selectedDateIso) {
    return (
      <p
        className="font-mono text-text-muted"
        style={{ fontSize: 12, letterSpacing: '0.04em', padding: 32, textAlign: 'center' }}
      >
        Pick a date to see available times.
      </p>
    );
  }

  // Derive the day header from the selected date — not from slot data — so it
  // appears immediately on date select and persists through loading and empty states.
  const dayLabel = formatLongDate(selectedDateIso, clientTimezone);

  // Compute slots-dependent values only when we actually have results.
  const groups =
    !loading && slotsForDay.length > 0 ? groupByPart(slotsForDay, clientTimezone) : [];
  const ownerOffset =
    !loading && slotsForDay.length > 0
      ? new Intl.DateTimeFormat('en-US', {
          timeZone: ownerTimezone,
          timeZoneName: 'short',
        })
          .formatToParts(new Date(selectedDateIso))
          .find((p) => p.type === 'timeZoneName')?.value
      : undefined;

  return (
    <div className="flex flex-col gap-6">
      {/* Day header — always shown once a date is selected */}
      <div>
        <h3
          className="font-display italic text-text"
          style={{
            fontSize: 24,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.05,
          }}
        >
          {dayLabel}
        </h3>
        <span
          className="font-mono text-text-secondary"
          style={{ fontSize: 12, letterSpacing: '0.04em' }}
        >
          your time · {clientTimezone}
        </span>
      </div>

      {/* Branch 2: loading — must come before the empty check so "NOTHING OPEN"
          is unreachable while a fetch is in flight */}
      {loading ? (
        <p
          className="font-mono text-text-muted"
          style={{ fontSize: 12, letterSpacing: '0.04em', padding: 32, textAlign: 'center' }}
        >
          LOADING SLOTS…
        </p>
      ) : slotsForDay.length === 0 ? (
        /* Branch 3: loaded but no slots for this day */
        <div className="flex flex-col items-center gap-3 py-12 text-center">
          <span
            className="font-mono"
            style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', color: 'var(--text-secondary)' }}
          >
            NOTHING OPEN
          </span>
          <p className="text-text-secondary" style={{ fontSize: 14 }}>
            No slots that day. Try a different date.
          </p>
        </div>
      ) : (
        /* Branch 4: has slots */
        <>
          {groups
            .filter((g) => g.slots.length > 0)
            .map((group) => (
              <div key={group.label}>
                <span
                  className="font-mono"
                  style={{
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    color: 'var(--text-caption)',
                  }}
                >
                  {group.label}
                </span>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
                  {group.slots.map((s) => {
                    const selected = selectedStartUtc === s.startUtc;
                    return (
                      <button
                        key={s.startUtc}
                        type="button"
                        onClick={() => onSelect(s)}
                        aria-pressed={selected}
                        className="contact-field transition-all book-slot"
                        style={{
                          padding: '12px 8px',
                          borderRadius: 'var(--radius-md)',
                          background: selected
                            ? 'linear-gradient(135deg, var(--accent), var(--indigo))'
                            : 'var(--bg-elevated)',
                          border: selected
                            ? '1px solid transparent'
                            : '1px solid var(--border)',
                          color: selected ? 'var(--bg-primary)' : 'var(--text-primary)',
                          fontFamily: 'var(--font-mono)',
                          fontSize: 13,
                          fontWeight: 700,
                          cursor: 'pointer',
                          letterSpacing: '0.02em',
                        }}
                      >
                        {formatTime(s.startUtc, clientTimezone)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

          <p
            className="font-mono text-text-secondary mt-2"
            style={{ fontSize: 12, letterSpacing: '0.04em' }}
          >
            Alejandro is in {ownerTimezone}
            {ownerOffset ? ` (${ownerOffset})` : ''}
          </p>
        </>
      )}
    </div>
  );
}
